import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import useAxiosHelper from "./../../Common/AxiosHelper";
import { ApiPaths } from "./../../Config/ApiPath";
import { BasicInfo, toastFailed, toastSuccess } from "./../../Config/BasicInfo";
import imageCompression from "browser-image-compression";
import "./SupportApp.css";
import moment from "moment/moment";
import {
  Headphones,
  Plus,
  ChevronRight,
  Upload,
  FileText,
  MessageCircle,
  Inbox,
  X,
  Send,
  Paperclip,
  Wallet,
  ArrowUpRight,
  Layers,
  User,
  HelpCircle,
} from "lucide-react";

const MAX_FILES = 10; // backend: upload.array('attachments', 10)
const STATUS_FILTERS = ["All", "Open", "In Progress", "Closed"];

// Shown when the admin hasn't configured ticket types — they only fill the subject
const SUGGESTED_TOPICS = [
  { label: "Deposit", Icon: Wallet },
  { label: "Withdrawal", Icon: ArrowUpRight },
  { label: "Package", Icon: Layers },
  { label: "Account", Icon: User },
  { label: "Other", Icon: HelpCircle },
];

const statusKey = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");

const Support = () => {
  const navigate = useNavigate();
  const { AxiosPost, AxiosGet } = useAxiosHelper();
  const [files, setFiles] = useState([]);
  const [ticketType, setTicketType] = useState("");
  const [ticketList, setTicketList] = useState();
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [ticketsData, setTicketsData] = useState(null); // null = loading
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("All");

  const onDrop = async (acceptedFiles) => {
    const room = MAX_FILES - files.length;
    if (room <= 0) {
      toastFailed(`You can attach up to ${MAX_FILES} files`);
      return;
    }
    const picked = acceptedFiles.slice(0, room);
    const compressedFiles = await Promise.all(
      picked.map(async (file) => {
        if (!file.type?.startsWith("image/")) return file;
        try {
          return await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true });
        } catch (err) {
          console.error("Error compressing image:", err);
          return file;
        }
      })
    );
    setFiles((prev) => [...prev, ...compressedFiles]);
    setPreviews((prev) => [
      ...prev,
      ...compressedFiles.map((file) => ({
        url: file.type?.startsWith("image/") ? URL.createObjectURL(file) : null,
        name: file.name || "file",
      })),
    ]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const resetForm = () => {
    setFiles([]);
    setPreviews([]);
    setTicketType("");
    setDescription("");
    setError(null);
  };

  const uploadFiles = async () => {
    setError(null);
    if (!ticketType.trim()) {
      setError("Choose a topic or enter a subject");
      return;
    }
    if (!description.trim()) {
      setError("Describe your issue so we can help");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append("attachments", file));
    formData.append("subject", ticketType.trim());
    formData.append("description", description.trim());

    try {
      const response = await AxiosPost(ApiPaths.createTicket, formData);
      BasicInfo.isDebug && console.log("Upload success:", response);
      resetForm();
      setShowCreate(false);
      toastSuccess(response?.message);
      FetchTickets();
    } catch (err) {
      console.error("Upload error:", err);
      setError(err?.response?.data?.error || "Failed to submit the ticket. Please try again.");
      toastFailed(err?.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    FetchTickets();
    FetchTicketsTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sheets: Escape closes, page behind stays put
  const sheetOpen = showCreate || !!selectedImage;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !uploading) {
        setShowCreate(false);
        setSelectedImage(null);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen, uploading]);

  const handleClearPreview = (index) => {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  async function FetchTickets() {
    try {
      const res = await AxiosGet(ApiPaths?.getTickets);
      setTicketsData(Array.isArray(res) ? res : []);
    } catch (error) {
      console.log(error);
      setTicketsData([]);
    }
  }

  async function FetchTicketsTypes() {
    try {
      const res = await AxiosGet(ApiPaths?.getTicketsType);
      setTicketList(res);
    } catch (error) {
      console.log(error);
    }
  }

  const handleChat = (ticketId, status) => {
    if (ticketId) {
      if (status) {
        navigate(`/tickets?${new URLSearchParams({ ticketId, status }).toString()}`);
      } else {
        toastFailed("Ticket is not active");
      }
    } else {
      toastFailed("Invalid ticket ID");
    }
  };

  const list = Array.isArray(ticketsData) ? ticketsData : [];
  // newest activity first
  const sorted = [...list].sort(
    (a, b) => new Date(b?.updated_at || b?.created_at || 0) - new Date(a?.updated_at || a?.created_at || 0)
  );
  const count = (s) => list.filter((x) => statusKey(x?.status) === statusKey(s)).length;
  const openCount = count("Open") + count("In Progress");
  const shown = filter === "All" ? sorted : sorted.filter((x) => statusKey(x?.status) === statusKey(filter));

  // Normalize ticket types (may be array of strings or objects) into options
  const ticketTypeOptions = (Array.isArray(ticketList) ? ticketList : [])
    .map((t) => (typeof t === "string" ? t : t?.name || t?.type || t?.title || t?.label || ""))
    .filter(Boolean);
  const topics = ticketTypeOptions.length
    ? ticketTypeOptions.map((label) => ({ label, Icon: MessageCircle }))
    : SUGGESTED_TOPICS;

  const lastReply = (t) => {
    const c = Array.isArray(t?.comments) ? t.comments[t.comments.length - 1] : null;
    return c ? { who: c.user_type === "admin" ? "Support" : "You", text: c.message } : null;
  };

  return (
    <section className="spPage">
      {/* ---------- Hero ---------- */}
      <div className="spHero">
        <div className="spHeroTop">
          <span className="spEyebrow">
            <Headphones size={13} /> Support
          </span>
        </div>
        <h1>How can we help?</h1>
        <p>Open a ticket and chat with our team. You'll see every reply right here.</p>
        <div className="spHeroStats">
          <div>
            <b>{list.length}</b>
            <small>Tickets</small>
          </div>
          <div>
            <b className="open">{openCount}</b>
            <small>Active</small>
          </div>
          <div>
            <b className="closed">{count("Closed")}</b>
            <small>Resolved</small>
          </div>
        </div>
        <button type="button" className="spNewBtn" onClick={() => setShowCreate(true)}>
          <Plus size={18} /> New ticket
        </button>
      </div>

      {/* ---------- Tickets ---------- */}
      <div className="spListHead">
        <h2>Your tickets</h2>
      </div>
      {list.length > 0 && (
        <div className="spFilters" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((s) => (
            <button key={s} type="button" className={`spChip ${filter === s ? "isOn" : ""}`} onClick={() => setFilter(s)}>
              {s}
              {s !== "All" && <i>{count(s)}</i>}
            </button>
          ))}
        </div>
      )}

      {ticketsData === null ? (
        <div className="spList">
          {[0, 1].map((i) => (
            <div key={i} className="spTicket spSkel">
              <span className="a" />
              <span className="b" />
            </div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="spEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>No tickets yet</b>
          <small>Have a question about a deposit, withdrawal or your package? We're here to help.</small>
          <button type="button" onClick={() => setShowCreate(true)}>
            <Plus size={15} /> Create a ticket
          </button>
        </div>
      ) : shown.length === 0 ? (
        <div className="spEmpty compact">
          <small>No {filter.toLowerCase()} tickets.</small>
        </div>
      ) : (
        <div className="spList">
          {shown.map((x, i) => {
            const sk = statusKey(x?.status);
            const reply = lastReply(x);
            const replies = x?.comments?.length || 0;
            return (
              <article
                key={x?.ticketId || i}
                className={`spTicket st-${sk}`}
                onClick={() => handleChat(x?.ticketId, x?.status)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleChat(x?.ticketId, x?.status)}
              >
                <div className="spTicketTop">
                  <span className="spTicketIcon">
                    <MessageCircle size={19} />
                  </span>
                  <span className="spTicketMain">
                    <b>{x?.subject || "Support ticket"}</b>
                    <small>
                      {moment(x?.updated_at || x?.created_at).fromNow()} · #{String(x?.ticketId || "").slice(0, 8)}
                    </small>
                  </span>
                  {x?.status && <span className={`spStatus ${sk}`}>{x.status}</span>}
                </div>

                <p className="spTicketDesc">{reply ? <><b>{reply.who}:</b> {reply.text}</> : x?.description}</p>

                <div className="spTicketFoot">
                  <span>
                    <MessageCircle size={13} /> {replies} {replies === 1 ? "reply" : "replies"}
                  </span>
                  {x?.priority && <span className={`spPriority ${statusKey(x.priority)}`}>{x.priority}</span>}
                  {x?.attachments?.length > 0 && (
                    <button
                      type="button"
                      className="spThumb"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(x.attachments[0]);
                      }}
                      aria-label="View attachment"
                    >
                      <img src={x.attachments[0]} alt="" />
                      {x.attachments.length > 1 && <em>+{x.attachments.length - 1}</em>}
                    </button>
                  )}
                  <span className="spOpen">
                    {sk === "closed" ? "View" : "Reply"} <ChevronRight size={15} />
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* ---------- Create ticket sheet ---------- */}
      {showCreate && (
        <div className="spOverlay" onClick={() => !uploading && setShowCreate(false)}>
          <div className="spSheet" role="dialog" aria-modal="true" aria-labelledby="spCreateTitle" onClick={(e) => e.stopPropagation()}>
            <span className="spHandle" aria-hidden="true" />
            <button type="button" className="spClose" onClick={() => setShowCreate(false)} disabled={uploading} aria-label="Close">
              <X size={18} />
            </button>
            <h3 id="spCreateTitle">New ticket</h3>
            <p className="spSheetSub">Tell us what's going on — we usually reply in the ticket chat.</p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                uploadFiles();
              }}
            >
              <div className="spField">
                <span>Topic</span>
                <div className="spTopics">
                  {topics.map(({ label, Icon }) => (
                    <button
                      key={label}
                      type="button"
                      className={`spTopic ${ticketType === label ? "isOn" : ""}`}
                      onClick={() => {
                        setError(null);
                        setTicketType(label);
                      }}
                    >
                      <Icon size={16} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {!ticketTypeOptions.length && (
                <label className="spField">
                  <span>Subject</span>
                  <input
                    type="text"
                    placeholder="Short summary of your issue"
                    value={ticketType}
                    onChange={(e) => {
                      setError(null);
                      setTicketType(e.target.value);
                    }}
                    maxLength={120}
                  />
                </label>
              )}

              <label className="spField">
                <span>
                  Description <em>{description.length}/1000</em>
                </span>
                <textarea
                  placeholder="Include amounts, dates and transaction IDs if relevant…"
                  value={description}
                  onChange={(e) => {
                    setError(null);
                    setDescription(e.target.value.slice(0, 1000));
                  }}
                  rows={5}
                />
              </label>

              <div className="spField">
                <span>
                  Attachments <em>{files.length}/{MAX_FILES}</em>
                </span>
                <div {...getRootProps({ className: `spDrop ${isDragActive ? "isActive" : ""}` })}>
                  <input {...getInputProps()} />
                  <span className="spDropIcon">
                    <Upload size={20} />
                  </span>
                  <span>
                    <b>Add screenshots or files</b>
                    <small>Tap to choose, or drop files here</small>
                  </span>
                </div>
                {previews.length > 0 && (
                  <div className="spPreviews">
                    {previews.map((p, index) => (
                      <div key={index} className="spPreview">
                        {p.url ? (
                          <img src={p.url} alt={`Attachment ${index + 1}`} />
                        ) : (
                          <span className="spFileTile">
                            <FileText size={20} />
                            <small>{p.name}</small>
                          </span>
                        )}
                        <button type="button" onClick={() => handleClearPreview(index)} aria-label={`Remove attachment ${index + 1}`}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && <p className="spError">{error}</p>}

              <button type="submit" className="spSubmit" disabled={uploading}>
                {uploading ? (
                  <>
                    <span className="spSpin" /> Submitting…
                  </>
                ) : (
                  <>
                    <Send size={17} /> Submit ticket
                  </>
                )}
              </button>
              <p className="spHint">
                <Paperclip size={12} /> Attachments help us resolve issues faster
              </p>
            </form>
          </div>
        </div>
      )}

      {/* ---------- Image viewer ---------- */}
      {selectedImage && (
        <div className="spViewer" onClick={() => setSelectedImage(null)}>
          <button type="button" className="spViewerClose" onClick={() => setSelectedImage(null)} aria-label="Close">
            <X size={20} />
          </button>
          <img src={selectedImage} alt="Attachment" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

    </section>
  );
};

export default Support;
